// apps/pos/src/components/_mixins/smartPayHelper.js
import uuid4 from "uuid4";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      smartPayApiEndpoint: "https://api-dev.smart-connect.cloud/POS",
      posRegisterId: uuid4(),
      posVendorName: "Beamy",
      currentTransId: null,
    };
  },

  computed: {
    ...mapGetters(["localSettings"]),

    smartpayTerminal() {
      if ("smartpay_terminal" in this.localSettings[0]) {
        return this.localSettings[0]["smartpay_terminal"];
      }
    },
  },

  methods: {
    encodeParameters(params) {
      return new URLSearchParams(params).toString();
    },

    getCommonHeaders() {
      return {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      };
    },

    getBaseParameters(useTerminal = false) {
      const baseParams = {
        POSVendorName: this.posVendorName,
        POSBusinessName: localStorage.getItem("company"),
      };

      if (useTerminal && this.smartpayTerminal) {
        return {
          ...baseParams,
          POSRegisterID: this.smartpayTerminal.POSRegisterID,
          POSRegisterName: this.smartpayTerminal.POSRegisterName,
        };
      }

      return {
        ...baseParams,
        POSRegisterID: this.posRegisterId,
      };
    },

    getTransactionParameters(transactionType, amountCents, extraParams = {}) {
      return {
        ...this.getBaseParameters(true),
        TransactionMode: "ASYNC",
        TransactionType: transactionType,
        AmountTotal: parseFloat(amountCents) * 100, // Amount in cents
        ...extraParams,
      };
    },

    async makeSmartPayRequest(url, method, parameters) {
      const encodedParams = this.encodeParameters(parameters);
      const response = await fetch(url, {
        method,
        body: encodedParams,
        headers: this.getCommonHeaders(),
      });
      return await response.json();
    },

    handleTransactionError(errorMessage, errorContext) {
      this.$notify({
        text: `SmartPay: ${errorMessage}`,
        type: "error",
        duration: 5000,
      });
      console.error(`Error ${errorContext}:`, error);
      this.eftposBusy = false;
    },

    async initialisePairing(code, registerName) {
      try {
        const parameters = {
          ...this.getBaseParameters(false),
          POSRegisterName: registerName,
        };
        
        const data = await this.makeSmartPayRequest(
          `${this.smartPayApiEndpoint}/Pairing/${code}`,
          "PUT",
          parameters
        );

        return {
          data: data,
          POSRegisterID: parameters.POSRegisterID,
          POSRegisterName: parameters.POSRegisterName,
        };
      } catch (error) {
        this.handleTransactionError("Pairing Failed", "initializing SmartPay pairing");
      }
    },

    async smartPayPurchase(amountCents) {
      this.eftposBusy = true;
      try {
        const parameters = this.getTransactionParameters("Card.Purchase", amountCents);
        const data = await this.makeSmartPayRequest(
          `${this.smartPayApiEndpoint}/Transaction`,
          "POST",
          parameters
        );
        
        this.currentTransId = data.transactionId;
        this.startPolling(data);
      } catch (error) {
        this.handleTransactionError("Purchase Failed", "during SmartPay purchase");
      }
    },

    async smartPayCashout(amountCents, cashoutAmountCents) {
      this.eftposBusy = true;
      try {
        const parameters = this.getTransactionParameters("Card.PurchasePlusCash", amountCents, {
          AmountCash: parseFloat(cashoutAmountCents) * 100, // Amount in cents
        });
        
        console.log("SmartPay Cashout parameters:", parameters);
        const data = await this.makeSmartPayRequest(
          `${this.smartPayApiEndpoint}/Transaction`,
          "POST",
          parameters
        );
        
        this.currentTransId = data.transactionId;
        this.startPolling(data);
      } catch (error) {
        console.log("Error during SmartPay cashout:", error);
        this.eftposBusy = false;
      }
    },

    async smartPayRefund(amountCents) {
      this.refunding = true;
      try {
        const parameters = this.getTransactionParameters("Card.Refund", amountCents);
        const data = await this.makeSmartPayRequest(
          `${this.smartPayApiEndpoint}/Transaction`,
          "POST",
          parameters
        );
        
        this.startPolling(data);
      } catch (error) {
        this.handleTransactionError("Refund Failed", "during SmartPay refund");
      }
    },

    startPolling(transactionData) {
      const maxAttempts = 15;
      let attempts = 0;

      const intervalId = setInterval(async () => {
        attempts++;

        try {
          const pollResult = await this.pollTransaction(transactionData);

          if (pollResult.transactionStatus === "COMPLETED") {
            this.handleTransactionResult(pollResult, transactionData, intervalId);
          } else if (attempts >= maxAttempts) {
            this.$notify({
              text: "SmartPay: Transaction timed out",
              type: "error",
              duration: 5000,
            });
            clearInterval(intervalId);
            this.eftposBusy = false;
          }
        } catch (error) {
          this.$notify({
            text: "SmartPay: Error during payment process",
            type: "error",
            duration: 5000,
          });
          clearInterval(intervalId);
          this.eftposBusy = false;
        }
      }, 5000);
    },

    handleTransactionResult(pollResult, transactionData, intervalId) {
      const amount = parseInt(transactionData.data.AmountTotal / 100);
      
      switch (pollResult.data.Result) {
        case "OK-ACCEPTED":
          this.handleSuccessfulTransaction(pollResult, amount, intervalId);
          break;
        case "OK-DECLINED":
          this.showNotificationAndCleanup(`${pollResult.data.Result}, ${pollResult.data.ResultText}`, "error", intervalId);
          break;
        case "OK-UNAVAILABLE":
          this.showNotificationAndCleanup(`${pollResult.data.Result}, ${pollResult.data.ResultText}`, "error", intervalId);
          break;
        case "CANCELLED":
          this.showNotificationAndCleanup(`${pollResult.data.Result}, ${pollResult.data.ResultText}`, "error", intervalId);
          break;
        case "FAILED":
        case "FAILED-INTERFACE":
          this.showNotificationAndCleanup(`${pollResult.data.Result}, ${pollResult.data.ResultText}`, "error", intervalId);
          break;
        default:
          this.$notify({
            text: "SmartPay: Unknown transaction result. Please try again.",
            type: "error",
            duration: 5000,
          });
          clearInterval(intervalId);
          this.eftposBusy = false;
          break;
      }
    },

    handleSuccessfulTransaction(pollResult, amount, intervalId) {
      const functionType = pollResult.data.Function;
      
      if (functionType === "Card.Purchase" || functionType === "Card.PurchasePlusCash") {
        this.addPayment({ type: "eftpos", value: amount, id: pollResult.transactionId });
        this.showNotificationAndCleanup("Payment Approved", "success", intervalId);
        this.completeTransaction();
      } else if (functionType === "Card.Refund") {
        this.addPayment({ type: "refund", refund_method: "eftpos", value: -1 * amount });
        this.showNotificationAndCleanup("Refund Approved", "success", intervalId);
        this.refunding = false;
      }
    },

    showNotificationAndCleanup(message, type, intervalId) {
      this.$notify({
        text: `SmartPay: ${message}`,
        type: type,
        duration: 5000,
      });
      clearInterval(intervalId);
      this.eftposBusy = false;
    },

    async pollTransaction(transactionData) {
      const pollingUrl = transactionData.data.PollingUrl;
      try {
        const response = await fetch(pollingUrl, {
          method: "GET",
        });
        return await response.json();
      } catch (error) {
        console.error("Error polling SmartPay transaction:", error);
      }
    },
  },
};